import { DrawnMol } from '@moleculeone/types';
import cx from 'classnames';
import { ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Atom } from './atom/Atom';
import { Bond, CUT_APPLIED, CUT_DESIRED } from './bond/Bond';
import { Gradient } from './Gradient';

declare const window: Window;

export const BOND_CUT_APPLIED = CUT_APPLIED;
export const BOND_CUT_DESIRED = CUT_DESIRED;

export type Disconnection = {
  type: string;
  backgroundClassName: string;
  bgColor: string;
  bgOpacity: number;
  color: string;
};

export type DisconnectionData = {
  [idx: string]: Disconnection;
};

export type RandomSeeds = {
  [seedId: string]: string;
};

export interface SVGMolProps {
  drawnMol: DrawnMol;
  idx?: string;
  className?: string;
  showWarnings?: boolean;
  isHighlighted?: boolean;
  before?: ReactNode;
  after?: ReactNode;
  preview?: boolean;
  cutData?: DisconnectionData;
}

// TODO: move this elsewhere
if (
  typeof window !== 'undefined' &&
  window.navigator.userAgent.toLowerCase().indexOf('chrome') > -1 &&
  window.navigator.platform !== 'MacIntel'
) {
  HTMLElement.prototype.getBoundingClientRect = function () {
    const rect = Element.prototype.getBoundingClientRect.call(this);
    if (this.closest('foreignObject')) {
      return {
        ...rect,
        x: rect.x / window.devicePixelRatio,
        y: rect.y / window.devicePixelRatio,
        left: rect.left / window.devicePixelRatio,
        top: rect.top / window.devicePixelRatio,
        width: rect.width / window.devicePixelRatio,
        height: rect.height / window.devicePixelRatio,
        right: rect.right / window.devicePixelRatio,
        bottom: rect.bottom / window.devicePixelRatio,
      };
    }
    return rect;
  };
}

export function SVGMol(props: SVGMolProps) {
  const {
    drawnMol,
    idx = uuidv4(),
    className,
    showWarnings = true,
    before,
    after,
    preview = false,
    cutData = {},
    ...restProps
  } = props;

  const { atoms, bonds } = drawnMol;

  const bondList: any = [];
  const gradientList: any = [];

  for (const bond of Object.values(bonds)) {
    const atom1 = atoms[bond.start];
    const atom2 = atoms[bond.end];
    const pos1 = atom1.pos;
    const pos2 = atom2.pos;
    const col1 = atom1.color || 'black';
    const col2 = atom2.color || 'black';
    const key = `${bond.start}-${bond.end}`;
    let cut;

    if (bond.cut) {
      cut = {
        type: CUT_APPLIED,
      };
    } else if (cutData[bond.idx]) {
      cut = {
        ...cutData[bond.idx],
        type: CUT_DESIRED,
      };
    }

    let color = bond.color;
    if (color == null) {
      if (col1 === col2) {
        color = col1;
      } else {
        const colId = `Mol-${idx}_LinearGradient_${key}`;
        color = `url('#${colId}')`;
        gradientList.push(
          <Gradient pos1={pos1} pos2={pos2} col1={col1} col2={col2} id={colId} key={`gradient-${key}`} />,
        );
      }
    }
    const warning = showWarnings && bond.warnings.length > 0;
    const title = warning ? bond.warnings[0] : 'Advanced actions: right mouse button';

    bondList.push(
      <Bond
        bond={bond}
        cutProps={cut}
        title={title}
        pos1={pos1}
        pos2={pos2}
        color={color}
        warning={warning}
        data-idx={bond.idx}
        data-start={atoms[bond.start].idx}
        data-end={atoms[bond.end].idx}
        data-test="svg-bond-group"
        key={`bond-group-${key}`}
      />,
    );
  }

  const atomList: any = [];
  for (const atom of Object.values(atoms)) {
    const warning = showWarnings && atom.warnings.length > 0;
    const title = warning ? atom.warnings[0] : 'Advanced actions: right mouse button';

    atomList.push(
      <Atom
        atom={atom}
        title={title}
        warning={warning}
        data-idx={atom.idx}
        data-test="svg-atom-group"
        key={`gradient-${atom.idx}`}
      />,
    );
  }
  return (
    <g className={cx(className, 'mol')} data-test={preview ? 'svg-preview' : 'svg-mol'} data-idx={idx} {...restProps}>
      <defs>{gradientList}</defs>
      {before}
      <g className="bonds">{bondList}</g>
      <g className="atoms" fontFamily="Arial, sans-serif" fontSize="56px" strokeWidth="15px" strokeLinejoin="round">
        {atomList}
      </g>
      {after}
    </g>
  );
}

SVGMol.randomSeeds = {} as RandomSeeds;
